import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SearchClinic, { SearchClinicRef } from './SearchClinic';
import { Clinic } from '../types/clinic';
// import { Insurance } from '../types/insurance';
import axios from 'axios';
import type { DatePickerProps } from 'antd';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

function CreateReferral() {
    // Get clinic name from URL path
    const { clinicName: clinicNameParam } = useParams<{ clinicName?: string }>();
    const location = useLocation();
    
    // Add ref for SearchClinic component
    const searchClinicRef = useRef<SearchClinicRef>(null);
    
    const [firstName, setFirstName] = useState<string>('');
    const [firstNameError, setFirstNameError] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [lastNameError, setLastNameError] = useState<string>('');
    const [dob, setDob] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [phoneError, setPhoneError] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [insuranceName, setInsuranceName] = useState<string>('');
    const [insuranceNameError, setInsuranceNameError] = useState<string>('');
    const [insuranceId, setInsuranceId] = useState<string>('');
    const [insuranceIdError, setInsuranceIdError] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [reasonError, setReasonError] = useState<string>('');
    const [providerName, setProviderName] = useState<string>('');
    const [providerNameError, setProviderNameError] = useState<string>('');
    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [otherProviderName, setOtherProviderName] = useState<string>('');
    const [providerPhone, setProviderPhone] = useState<string>('');
    const [providerPhoneError, setProviderPhoneError] = useState<string>('');
    const [clinicName, setClinicName] = useState<string>('');
    const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showSearchClinic, setShowSearchClinic] = useState<boolean>(true);
    const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    
    // New state for insurance options from API
    // const [insuranceOptions, setInsuranceOptions] = useState<Insurance[]>([]);
    // const [isLoadingInsurances, setIsLoadingInsurances] = useState<boolean>(false);
    
    // Custom organization fields
    const [referringPartyName, setReferringPartyName] = useState<string>('');
    const [referringPartyPhone, setReferringPartyPhone] = useState<string>('');
    const [referringPartyEmail, setReferringPartyEmail] = useState<string>('');
    const [referringPartyNameError, setReferringPartyNameError] = useState<string>('');
    const [referringPartyPhoneError, setReferringPartyPhoneError] = useState<string>('');
    const [referringPartyEmailError, setReferringPartyEmailError] = useState<string>('');
    const [isCustomOrganization, setIsCustomOrganization] = useState<boolean>(false);
    
    // Case Manager fields for insurer or Non-Profit
    const [caseManagerName, setCaseManagerName] = useState<string>('');
    const [caseManagerPhone, setCaseManagerPhone] = useState<string>('');
    const [caseManagerEmail, setCaseManagerEmail] = useState<string>('');
    const [caseManagerNameError, setCaseManagerNameError] = useState<string>('');
    const [caseManagerPhoneError, setCaseManagerPhoneError] = useState<string>('');
    const [caseManagerEmailError, setCaseManagerEmailError] = useState<string>('');

    // Add new state variables after other state declarations
    const [expectedPostpartumDueDate, setExpectedPostpartumDueDate] = useState<string>('');
    const [expectedPostpartumDueDateError, setExpectedPostpartumDueDateError] = useState<string>('');
    const [postpartumRisk, setPostpartumRisk] = useState<string>('');
    const [postpartumRiskError, setPostpartumRiskError] = useState<string>('');
    
    // Add ref for DatePicker component
    const dobDatePickerRef = useRef<any>(null);
    
    const [formKey, setFormKey] = useState<number>(0); // Add a key to force remount components
    
    // Reset clinic state when URL changes
    useEffect(() => {
        // Reset clinic state when location changes
        setSelectedClinic(null);
        setClinicName('');
        setShowSearchClinic(true);
        
        // Reset provider selection when clinic changes
        setProviderName('');
        setSelectedProvider('');
        setOtherProviderName('');
        
        // Reset custom organization fields
        setIsCustomOrganization(false);
        setReferringPartyName('');
        setReferringPartyPhone('');
        setReferringPartyEmail('');
        setReferringPartyNameError('');
        setReferringPartyPhoneError('');
        setReferringPartyEmailError('');
        
        // Reset case manager fields
        setCaseManagerName('');
        setCaseManagerPhone('');
        setCaseManagerEmail('');
        setCaseManagerNameError('');
        setCaseManagerPhoneError('');
        setCaseManagerEmailError('');
        
        // This will force the component to reprocess the URL on each navigation
    }, [location.pathname, clinicNameParam]);

    // Process URL params and search for clinic
    useEffect(() => {
        // Function to extract clinic name from path
        const extractClinicNameFromPath = () => {
            const pathSegments = location.pathname.split('/');
            
            // If path is like /clinic-name (direct path)
            if (pathSegments.length === 2 && pathSegments[1]) {
                // Ensure proper URL decoding for spaces (%20) and other special characters
                try {
                    const decodedName = decodeURIComponent(pathSegments[1]).replace(/-/g, ' ');
                    return decodedName;
                } catch (e) {
                    console.error('Error decoding URL path:', e);
                    return pathSegments[1].replace(/-/g, ' ');
                }
            }
            
            return null;
        };

        // Extract clinic code from URL path
        const extractClinicCodeFromPath = () => {
            const pathSegments = location.pathname.split('/');
            
            // If path is like /test-clinic (direct path where test-clinic is clinicCode)
            if (pathSegments.length === 2 && pathSegments[1]) {
                try {
                    // Return the raw segment as clinic code (no transformation needed)
                    return decodeURIComponent(pathSegments[1]);
                } catch (e) {
                    console.error('Error decoding URL path for clinic code:', e);
                    return pathSegments[1];
                }
            }
            
            return null;
        };

        // Try to get clinic name from path parameter or extract from URL
        // Use consistent approach for spaces - replace dashes with spaces for both methods
        let urlClinicName = null;
        
        if (clinicNameParam) {
            try {
                urlClinicName = decodeURIComponent(clinicNameParam).replace(/-/g, ' ');
            } catch (e) {
                console.error('Error decoding URL param:', e);
                urlClinicName = clinicNameParam.replace(/-/g, ' ');
            }
        } else {
            // urlClinicName = extractClinicNameFromPath();
            const urlClinicCode = extractClinicCodeFromPath();
            if (urlClinicCode) {
                urlClinicName = urlClinicCode; // For backward compatibility
            } else {
                urlClinicName = extractClinicNameFromPath();
            }
        }
        
        if (urlClinicName) {
            // Clear any previous clinic selection from localStorage when URL contains a clinic
            localStorage.removeItem('selectedClinic');
            
            // Reset clinic state to ensure clean selection
            setSelectedClinic(null);
            setShowSearchClinic(true);
            
            // Search for clinic using the SearchClinic component's data
            if (searchClinicRef.current) {
                // Set input directly in the search component to trigger its search
                searchClinicRef.current.setSearchInput?.(urlClinicName);
                
                // Try to find the clinic in the available clinics from the SearchClinic component
                if (searchClinicRef.current.getClinics) {
                    const clinics = searchClinicRef.current.getClinics();
                    if (clinics && clinics.length > 0) {
                        const urlClinicCode = extractClinicCodeFromPath();
                        if (urlClinicCode) {
                            findClinicByCode(clinics, urlClinicCode);
                        } else {
                            findClinicByName(clinics, urlClinicName);
                        }
                    } else {
                        // If clinics not yet loaded, wait for them to be available
                        searchClinicRef.current.onClinicsLoaded = (loadedClinics: any[]) => {
                            const urlClinicCode = extractClinicCodeFromPath();
                            if (urlClinicCode) {
                                findClinicByCode(loadedClinics, urlClinicCode);
                            } else {
                                findClinicByName(loadedClinics, urlClinicName);
                            }
                        };
                    }
                }
            }
        } else {
            // Regular flow - check for clinic in local storage
            const savedClinic = localStorage.getItem('selectedClinic');
            if (savedClinic) {
                const clinic: Clinic = JSON.parse(savedClinic);
                setClinicName(clinic.name);
                setSelectedClinic(clinic);
                setShowSearchClinic(false);
                
                // Check if this is the custom organization option (when name is "Other" and speciality is "custom")
                if (clinic.speciality === 'custom') {
                    setIsCustomOrganization(true);
                    // Clear any existing insurer or Non-Profit data
                    setInsuranceName('');
                } else {
                    setIsCustomOrganization(false);
                    // When a clinic with speciality 'Insurer' or 'Non-Profit' is selected, populate the Insurance Provider field
                    if (clinic.speciality?.toLowerCase() === 'insurer' || clinic.speciality?.toLowerCase() === 'non-profit') {
                        setInsuranceName(clinic.name);
                    }
                }
            }
        }
    }, [clinicNameParam, location.pathname]);

    // Fetch insurance options from API
    // useEffect(() => {
    //     const fetchInsuranceOptions = async () => {
    //         try {
    //             setIsLoadingInsurances(true);
    //             const response = await axios.get('/api/insurances', {
    //                 headers: {
    //                     'x-api-key': import.meta.env.VITE_REACT_APP_REFERRAL_API_KEY
    //                 }
    //             });
                
    //             if (response.data && Array.isArray(response.data)) {
    //                 // Sort insurance options alphabetically by name
    //                 const sortedInsurances = [...response.data].sort((a, b) => 
    //                     a.name.localeCompare(b.name)
    //                 );
    //                 setInsuranceOptions(sortedInsurances);
    //             }
    //         } catch (error) {
    //             console.error('Error fetching insurance options:', error);
    //         } finally {
    //             setIsLoadingInsurances(false);
    //         }
    //     };

    //     fetchInsuranceOptions();
    // }, []);

    // Helper function to find clinic by name from a list of clinics
    const findClinicByName = (clinics: any[], urlClinicName: string) => {
        // Normalize clinic name for comparison (remove all spaces and convert to lowercase)
        const normalizedSearchName = urlClinicName.toLowerCase().replace(/\s+/g, '');
        
        // First try an exact match (case insensitive and space-insensitive)
        let foundClinic = clinics.find((clinic: any) => {
            const clinicName = clinic.name || '';
            const normalizedClinicName = clinicName.toLowerCase().replace(/\s+/g, '');
            return normalizedClinicName === normalizedSearchName;
        });
        
        // If exact match not found, try a substring match
        if (!foundClinic) {
            foundClinic = clinics.find((clinic: any) => {
                const clinicName = clinic.name || '';
                const normalizedClinicName = clinicName.toLowerCase().replace(/\s+/g, '');
                return normalizedClinicName.includes(normalizedSearchName) || 
                       normalizedSearchName.includes(normalizedClinicName);
            });
        }
        
        if (foundClinic) {
            // Normalize the clinic data
            const normalizedClinic = {
                name: foundClinic.name || '',
                address: foundClinic.address || '',
                phone: foundClinic.phone || '',
                fax: foundClinic.fax || '',
                clinicianNames: Array.isArray(foundClinic.clinicianNames) 
                    ? foundClinic.clinicianNames 
                    : (foundClinic.clinicians || []),
                speciality: foundClinic.speciality || '',
                clinicLogo: foundClinic.clinicLogo || foundClinic.logo || '',
                clinicCode: foundClinic.clinicCode || '',
            };
            
            // Use the handleClinicSelect function to set the clinic
            handleClinicSelect(normalizedClinic);
        } else {
            console.log('No clinic found with name:', urlClinicName);
        }
    };

    // Helper function to find clinic by clinicCode
    const findClinicByCode = (clinics: any[], clinicCode: string) => {
        // Find clinic with matching clinicCode
        const foundClinic = clinics.find((clinic: any) => {
            return clinic.clinicCode === clinicCode;
        });
        
        if (foundClinic) {
            // Normalize the clinic data
            const normalizedClinic = {
                name: foundClinic.name || '',
                address: foundClinic.address || '',
                phone: foundClinic.phone || '',
                fax: foundClinic.fax || '',
                clinicianNames: Array.isArray(foundClinic.clinicianNames) 
                    ? foundClinic.clinicianNames 
                    : (foundClinic.clinicians || []),
                speciality: foundClinic.speciality || '',
                clinicLogo: foundClinic.clinicLogo || foundClinic.logo || '',
                clinicCode: foundClinic.clinicCode || '',
            };
            
            // Use the handleClinicSelect function to set the clinic
            handleClinicSelect(normalizedClinic);
        } else {
            console.log('No clinic found with code:', clinicCode);
        }
    };

    // Function to handle clinic selection from the SearchClinic component
    const handleClinicSelect = (clinic: Clinic) => {
        setClinicName(clinic.name);
        setSelectedClinic(clinic);
        localStorage.setItem('selectedClinic', JSON.stringify(clinic));
        setShowSearchClinic(false);
        
        // Dispatch a custom event to notify other components
        const event = new CustomEvent('clinicSelected', { detail: clinic });
        window.dispatchEvent(event);
        
        // Reset provider selection when a new clinic is selected
        setProviderName('');
        setSelectedProvider('');
        setOtherProviderName('');
        
        // Reset case manager information
        setCaseManagerName('');
        setCaseManagerPhone('');
        setCaseManagerEmail('');
        setCaseManagerNameError('');
        setCaseManagerPhoneError('');
        setCaseManagerEmailError('');
        
        // Check if this is the custom organization option
        if (clinic.speciality === 'custom') {
            setIsCustomOrganization(true);
            // Clear any existing insurer or Non-Profit data
            setInsuranceName('');
        } else {
            setIsCustomOrganization(false);
            // When a clinic with speciality 'Insurer' or 'Non-Profit' is selected, populate the Insurance Provider field
            if (clinic.speciality?.toLowerCase() === 'insurer' || clinic.speciality?.toLowerCase() === 'non-profit') {
                setInsuranceName(clinic.name);
            }
        }
    };

    // Function to clear the selected clinic
    const handleClearClinic = () => {
        // If the clinic being cleared was an insurer or Non-Profit, also clear the insurance name
        if (selectedClinic?.speciality?.toLowerCase() === 'insurer' || selectedClinic?.speciality?.toLowerCase() === 'non-profit') {
            setInsuranceName('');
        }
        
        // Reset provider selection when clinic is cleared
        setProviderName('');
        setSelectedProvider('');
        setOtherProviderName('');
        
        // Reset custom organization fields
        setReferringPartyName('');
        setReferringPartyPhone('');
        setReferringPartyEmail('');
        setReferringPartyNameError('');
        setReferringPartyPhoneError('');
        setReferringPartyEmailError('');
        setIsCustomOrganization(false);
        
        // Reset case manager fields
        setCaseManagerName('');
        setCaseManagerPhone('');
        setCaseManagerEmail('');
        setCaseManagerNameError('');
        setCaseManagerPhoneError('');
        setCaseManagerEmailError('');
        
        setClinicName('');
        setSelectedClinic(null);
        // Clear local storage
        localStorage.removeItem('selectedClinic');
        
        // Dispatch a custom event to notify other components
        const event = new CustomEvent('clinicCleared');
        window.dispatchEvent(event);
        
        // Clear cookie by setting its expiration date to the past
        document.cookie = "clinicName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        setShowSearchClinic(true);
    };

    // Add handleClearForm function
    const handleClearForm = () => {
        setFirstName('');
        setLastName('');
        setDob('');
        
        // Reset the DatePicker component if ref is available
        if (dobDatePickerRef.current) {
            dobDatePickerRef.current.blur();
            dobDatePickerRef.current.setValue(null);
        }

        if (selectedClinic?.speciality?.toLowerCase() !== 'insurer' && selectedClinic?.speciality?.toLowerCase() !== 'non-profit') {
            setInsuranceName('');
        }
        
        setPhone('');
        setEmail('');
        setReason('');
        setProviderName('');
        setSelectedProvider('');
        setOtherProviderName('');
        setProviderPhone('');
        setInsuranceId('');
        
        // Reset custom organization fields
        setReferringPartyName('');
        setReferringPartyPhone('');
        setReferringPartyEmail('');
        
        // Reset case manager fields
        setCaseManagerName('');
        setCaseManagerPhone('');
        setCaseManagerEmail('');
        
        // Clear search in SearchClinic component if it's visible
        if (showSearchClinic && searchClinicRef.current) {
            searchClinicRef.current.clearSearch();
        }
        
        // Clear all validation errors
        setFirstNameError('');
        setLastNameError('');
        setPhoneError('');
        setEmailError('');
        setInsuranceNameError('');
        setInsuranceIdError('');
        setProviderNameError('');
        setProviderPhoneError('');
        setReasonError('');
        setReferringPartyNameError('');
        setReferringPartyPhoneError('');
        setReferringPartyEmailError('');
        setCaseManagerNameError('');
        setCaseManagerPhoneError('');
        setCaseManagerEmailError('');
        
        // Note: We don't clear the clinic selection since that's a separate workflow
        setExpectedPostpartumDueDate('');
        setExpectedPostpartumDueDateError('');
        setPostpartumRisk('');
        setPostpartumRiskError('');
        
        // Force DatePicker to remount by changing the key
        setFormKey(prevKey => prevKey + 1);
    };

    // Validate input to prevent special characters (except dots)
    const validateNoSpecialChars = (value: string, errorSetter: React.Dispatch<React.SetStateAction<string>>) => {
        // Regex allows letters, numbers, spaces, and dots only
        const regex = /^[a-zA-Z0-9\s.]*$/;
        
        if (!value.trim()) {
            errorSetter('');
            return true;
        }
        
        if (regex.test(value)) {
            errorSetter('');
            return true;
        } else {
            errorSetter('Special characters are not allowed except for periods (.)');
            return false;
        }
    };
    
    // Validate email with special validation since emails need @ and other characters
    const validateEmail = (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return true;
        }
        
        // Basic email validation pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
            setEmailError('');
            return true;
        } else {
            setEmailError('Please enter a valid email address');
            return false;
        }
    };

    // Handle text input changes with validation
    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setFirstName(newValue);
        validateNoSpecialChars(newValue, setFirstNameError);
    };
    
    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLastName(newValue);
        validateNoSpecialChars(newValue, setLastNameError);
    };
    
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setEmail(newValue);
        validateEmail(newValue);
    };

    const onDOBChange: DatePickerProps['onChange'] = (_, dateString) => {
        setDob(dateString as string);
    };

    const handleExpectedPostpartumDueDateChange: DatePickerProps['onChange'] = (_, dateString) => {
        setExpectedPostpartumDueDate(dateString as string);
    };
    
    // Handle dropdown selection for provider
    const handleProviderSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        setSelectedProvider(selectedValue);
        
        if (selectedValue === 'Other') {
            // If "Other" is selected, keep the providerName field empty
            setProviderName('');
        } else if (selectedValue !== '') {
            // If a provider is selected, set the providerName
            setProviderName(selectedValue);
            setProviderNameError('');
        } else {
            // If empty is selected, clear the provider name
            setProviderName('');
        }
    };
    
    // Handle input for "Other" provider name
    const handleOtherProviderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setOtherProviderName(newValue);
        setProviderName(newValue); // Set the actual provider name field for form submission
        validateNoSpecialChars(newValue, setProviderNameError);
    };
    
    const handleInsuranceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInsuranceName(newValue);
        validateNoSpecialChars(newValue, setInsuranceNameError);
    };
    
    // const handleInsuranceNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //     const newValue = e.target.value;
    //     setInsuranceName(newValue);
    //     validateNoSpecialChars(newValue, setInsuranceNameError);
    // };
    
    const handleInsuranceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInsuranceId(newValue);
        validateNoSpecialChars(newValue, setInsuranceIdError);
    };
    
    const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setReason(newValue);
        validateNoSpecialChars(newValue, setReasonError);
    };

    // Validate phone number
    const validatePhone = (phoneNumber: string) => {
        // Remove all non-numeric characters for validation
        const phoneDigits = phoneNumber.replace(/\D/g, '');
        
        // Check if it's empty (will be handled by required attribute)
        if (!phoneNumber.trim()) {
            setPhoneError('');
            return true;
        }
        
        // Basic validation - must be 10 digits for US number
        if (phoneDigits.length === 10) {
            setPhoneError('');
            return true;
        } else {
            setPhoneError('Please enter a valid 10-digit phone number');
            return false;
        }
    };
    
    // Handle phone input with validation
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove all non-digit characters
        const digitsOnly = e.target.value.replace(/\D/g, '');
        
        // Limit to 10 digits
        const truncatedValue = digitsOnly.slice(0, 10);
        
        // Format the phone number as (XXX) XXX-XXXX
        let formattedValue = '';
        if (truncatedValue.length > 0) {
            formattedValue = '(' + truncatedValue.slice(0, 3);
            if (truncatedValue.length > 3) {
                formattedValue += ') ' + truncatedValue.slice(3, 6);
                if (truncatedValue.length > 6) {
                    formattedValue += '-' + truncatedValue.slice(6, 10);
                }
            }
        }
        
        setPhone(formattedValue);
        validatePhone(formattedValue);
    };

    

    const handlePostpartumRiskChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value;
        setPostpartumRisk(newValue);
        if (!newValue.trim()) {
            setPostpartumRiskError('Please select a risk level');
        } else {
            setPostpartumRiskError('');
        }
    };

    // Add new handler for provider phone
    const handleProviderPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove all non-digit characters
        const digitsOnly = e.target.value.replace(/\D/g, '');
        
        // Limit to 10 digits
        const truncatedValue = digitsOnly.slice(0, 10);
        
        // Format the phone number as (XXX) XXX-XXXX
        let formattedValue = '';
        if (truncatedValue.length > 0) {
            formattedValue = '(' + truncatedValue.slice(0, 3);
            if (truncatedValue.length > 3) {
                formattedValue += ') ' + truncatedValue.slice(3, 6);
                if (truncatedValue.length > 6) {
                    formattedValue += '-' + truncatedValue.slice(6, 10);
                }
            }
        }
        
        setProviderPhone(formattedValue);
        // Use the same validation but save to a different error state
        const phoneDigits = formattedValue.replace(/\D/g, '');
        if (!formattedValue.trim()) {
            setProviderPhoneError('');
        } else if (phoneDigits.length === 10) {
            setProviderPhoneError('');
        } else {
            setProviderPhoneError('Please enter a valid 10-digit phone number');
        }
    };

    // Check if form is valid (all required fields filled and no validation errors)
    useEffect(() => {
        const isPhoneValid = phone?.trim() === '' || validatePhone(phone);
        const isFirstNameValid = firstName.trim() !== '' && !firstNameError;
        const isLastNameValid = lastName.trim() !== '' && !lastNameError;
        const isEmailValid = email.trim() !== '' && !emailError;
        const isReasonValid = !reasonError;
        const isProviderNameValid = !providerNameError;
        const isInsuranceNameValid = !insuranceNameError;
        
        // Insurance ID is required when clinic speciality is Insurer or Non-Profit
        const isInsurerSelected = selectedClinic?.speciality?.toLowerCase() === 'insurer' || selectedClinic?.speciality?.toLowerCase() === 'non-profit';
        
        // Add a condition to check if insurance ID is valid based on whether it's required or not
        const isInsuranceIdValid = isInsurerSelected ? 
            (insuranceId.trim() !== '' && !insuranceIdError) : 
            !insuranceIdError;
        
        // Custom organization validation
        let isReferringPartyValid = true;
        if (isCustomOrganization) {
            const isReferringPartyNameValid = referringPartyName.trim() !== '' && !referringPartyNameError;
            const isReferringPartyPhoneValid = referringPartyPhone.trim() !== '' && !referringPartyPhoneError;
            const isReferringPartyEmailValid = referringPartyEmail.trim() !== '' && !referringPartyEmailError;
            
            isReferringPartyValid = isReferringPartyNameValid && isReferringPartyPhoneValid && isReferringPartyEmailValid;
        }
        
        // Case Manager validation for insurer or Non-Profit specialty
        let isCaseManagerValid = true;
        if (isInsurerSelected) {
            const isCaseManagerNameValid = caseManagerName.trim() !== '' && !caseManagerNameError;
            const isCaseManagerPhoneValid = caseManagerPhone.trim() !== '' && !caseManagerPhoneError;
            const isCaseManagerEmailValid = caseManagerEmail.trim() !== '' && !caseManagerEmailError;
            
            isCaseManagerValid = isCaseManagerNameValid && isCaseManagerPhoneValid && isCaseManagerEmailValid;
        }
        
        const isValid = 
            isFirstNameValid && 
            isLastNameValid && 
            dob !== '' && 
            phone?.trim() !== '' && 
            isPhoneValid &&
            isEmailValid && 
            isReasonValid && 
            isProviderNameValid &&
            isInsuranceNameValid &&
            isInsuranceIdValid &&  // Include insurance ID validation in the overall form validation
            clinicName?.trim() !== '' &&
            isReferringPartyValid &&
            isCaseManagerValid;

        setIsFormValid(isValid);
    }, [
        firstName, lastName, dob, phone, email, reason, clinicName,
        firstNameError, lastNameError, phoneError, emailError, reasonError,
        providerNameError, insuranceNameError, insuranceIdError, selectedClinic,
        referringPartyName, referringPartyPhone, referringPartyEmail,
        referringPartyNameError, referringPartyPhoneError, referringPartyEmailError,
        caseManagerName, caseManagerPhone, caseManagerEmail,
        caseManagerNameError, caseManagerPhoneError, caseManagerEmailError,
        isCustomOrganization, selectedProvider, otherProviderName, providerName,
        expectedPostpartumDueDate, postpartumRisk, providerPhone, providerPhoneError,
        insuranceId  // Make sure insuranceId is included in the dependency array
    ]);

    // Handlers for referring party fields
    const handleReferringPartyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setReferringPartyName(newValue);
        validateNoSpecialChars(newValue, setReferringPartyNameError);
    };
    
    const handleReferringPartyPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove all non-digit characters
        const digitsOnly = e.target.value.replace(/\D/g, '');
        
        // Limit to 10 digits
        const truncatedValue = digitsOnly.slice(0, 10);
        
        // Format the phone number as (XXX) XXX-XXXX
        let formattedValue = '';
        if (truncatedValue.length > 0) {
            formattedValue = '(' + truncatedValue.slice(0, 3);
            if (truncatedValue.length > 3) {
                formattedValue += ') ' + truncatedValue.slice(3, 6);
                if (truncatedValue.length > 6) {
                    formattedValue += '-' + truncatedValue.slice(6, 10);
                }
            }
        }
        
        setReferringPartyPhone(formattedValue);
        // Use the same validation but save to a different error state
        const phoneDigits = formattedValue.replace(/\D/g, '');
        if (!formattedValue.trim()) {
            setReferringPartyPhoneError('');
        } else if (phoneDigits.length === 10) {
            setReferringPartyPhoneError('');
        } else {
            setReferringPartyPhoneError('Please enter a valid 10-digit phone number');
        }
    };
    
    const handleReferringPartyEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setReferringPartyEmail(newValue);
        if (!newValue.trim()) {
            setReferringPartyEmailError('');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(newValue)) {
                setReferringPartyEmailError('');
            } else {
                setReferringPartyEmailError('Please enter a valid email address');
            }
        }
    };

    // Handlers for case manager fields
    const handleCaseManagerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCaseManagerName(newValue);
        validateNoSpecialChars(newValue, setCaseManagerNameError);
    };
    
    const handleCaseManagerPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove all non-digit characters
        const digitsOnly = e.target.value.replace(/\D/g, '');
        
        // Limit to 10 digits
        const truncatedValue = digitsOnly.slice(0, 10);
        
        // Format the phone number as (XXX) XXX-XXXX
        let formattedValue = '';
        if (truncatedValue.length > 0) {
            formattedValue = '(' + truncatedValue.slice(0, 3);
            if (truncatedValue.length > 3) {
                formattedValue += ') ' + truncatedValue.slice(3, 6);
                if (truncatedValue.length > 6) {
                    formattedValue += '-' + truncatedValue.slice(6, 10);
                }
            }
        }
        
        setCaseManagerPhone(formattedValue);
        // Use the same validation but save to a different error state
        const phoneDigits = formattedValue.replace(/\D/g, '');
        if (!formattedValue.trim()) {
            setCaseManagerPhoneError('');
        } else if (phoneDigits.length === 10) {
            setCaseManagerPhoneError('');
        } else {
            setCaseManagerPhoneError('Please enter a valid 10-digit phone number');
        }
    };
    
    const handleCaseManagerEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCaseManagerEmail(newValue);
        if (!newValue.trim()) {
            setCaseManagerEmailError('');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(newValue)) {
                setCaseManagerEmailError('');
            } else {
                setCaseManagerEmailError('Please enter a valid email address');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);

            // Helper function to strip phone formatting
            const stripPhoneFormatting = (phoneNumber: string) => {
                return phoneNumber.replace(/\D/g, '');
            };

            const submissionData = {
                firstName, 
                lastName, 
                dob, 
                phone: stripPhoneFormatting(phone), 
                email, 
                insuranceName, 
                insuranceId, 
                reason, 
                providerName, 
                clinicName,
                ...(isCustomOrganization ? {
                    referringPartyName,
                    referringPartyPhone: stripPhoneFormatting(referringPartyPhone),
                    referringPartyEmail
                } : {}),
                ...((selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                        || selectedClinic?.speciality?.toLowerCase() === 'non-profit') ? {
                    caseManagerName,
                    caseManagerPhone: stripPhoneFormatting(caseManagerPhone),
                    caseManagerEmail,
                    expectedPostpartumDueDate,
                    postpartumRisk,
                    providerPhone: stripPhoneFormatting(providerPhone)
                } : {})
            };
            
            // Create referral API call:
            await axios.post('/api/referrals', submissionData, {
                headers: {
                    'x-api-key': import.meta.env.VITE_REACT_APP_REFERRAL_API_KEY
                }
            });
            setShowSuccessDialog(true);
            setIsSubmitting(false);
            
            // Reset form
            handleClearForm();
        } catch (error) {
            console.error('Error creating referral:', error);
            setIsSubmitting(false);
        }
    };

    return (
        //<div className="flex justify-center items-center min-h-screen bg-blue-50 p-4 max-w-screen-xl mx-auto">
            
        <> 
            {/* Remove Clinic Logo Display as it's now shown in the App header */}
            
            {/* Success Toast Notification */}
            {showSuccessDialog && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-[#5d5c5cd2] bg-opacity-30 animate-fadeIn">
                    <div className="bg-white p-6 rounded-lg shadow-xl border-l-4 border-green-500 max-w-md w-full mx-4">
                        <div className="flex items-start mb-4">
                            <div className="bg-green-100 rounded-full p-2 mr-3 flex-shrink-0">
                                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-1">Referral Submitted Successfully!</h3>
                            </div>
                        </div>
                        <div className="flex justify-center mt-5">
                            <button
                                onClick={() => setShowSuccessDialog(false)}
                                style={{ padding: '0.8rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem', backgroundColor: '#1d4a46', color: '#FFF' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-0 w-full max-w-[680px] rounded-lg shadow-md" style={{ backgroundColor: "#FFF" }}>
                {/* Fixed header */}
                <div className="px-4 md:px-6 py-2 md:py-4 border-b border-gray-200 sticky top-0 bg-[#FFF] z-10 rounded-t-lg">
                    <p className="text-2xl md:text-3xl font-bold text-gray-800 text-center">Refer a Patient</p>
                    <p className="text-sm text-gray-600 text-center md:mt-2">
                        We value collaborative care—if you refer patients to LunaJoy, feel free to reach out about becoming a partner provider. This allows you to receive updates on your patient's treatment journey. Please call/text 727-761-0664 or email <a href="mailto:liaison@hellolunajoy.com">liaison@hellolunajoy.com</a>
                    </p>
                    
                </div>
                
                <form onSubmit={handleSubmit} className="flex flex-col">
                    {/* Scrollable content */}
                    <div 
                        className="overflow-y-scroll p-4 md:p-6 max-h-[72vh] md:max-h-[68vh] form-container"
                        style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#1d4a46 #f1f1f1',
                        }}
                    >
                        <style>
                            {`
                                ::-webkit-scrollbar {
                                    width: 6px;
                                }
                                ::-webkit-scrollbar-track {
                                    background: #f1f1f1;
                                    border-radius: 10px;
                                }
                                ::-webkit-scrollbar-thumb {
                                    background: #1d4a46;
                                    border-radius: 10px;
                                }
                                ::-webkit-scrollbar-thumb:hover {
                                    background: #2a6b64;
                                }
                            `}
                        </style>
                        <div className="p-4 mb-6" style={{ border: 'none', borderLeft: '4px solid #2b5750', paddingTop: '0' }}>
                            {clinicName && !showSearchClinic ? (
                                <div>
                                    <div className="bg-white rounded flex justify-between items-center mb-4">
                                        <p className="text-gray-600">
                                            Referral from: <span className="font-bold">{selectedClinic?.name === "Other" ? "Other" : clinicName}</span>
                                        </p>
                                        <div 
                                            onClick={handleClearClinic}
                                            title="Remove selected referring party"
                                            aria-label="Remove selected referring party"
                                            className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer ml-2"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </div>
                                    </div>
                                    
                                    {/* Custom Organization Fields moved directly into parent section */}
                                    {isCustomOrganization && (
                                        <>
                                            <div className="grid grid-cols-1 gap-4 mt-4 mb-4">
                                                <div className="text-left">
                                                    <p className="text-sm text-gray-600 mb-3">
                                                        We value collaborative care—please enter your details below which allows us to send you updates on your patient's journey.
                                                    </p>
                                                    <label htmlFor="referringPartyName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        Referring Party Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="referringPartyName"
                                                            type="text"
                                                            value={referringPartyName}
                                                            onChange={handleReferringPartyNameChange}
                                                            required
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                referringPartyNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                        />
                                                        {referringPartyNameError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {referringPartyNameError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="text-left">
                                                        <label htmlFor="referringPartyPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                            Referring Party Phone <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                id="referringPartyPhone"
                                                                type="tel"
                                                                value={referringPartyPhone}
                                                                onChange={handleReferringPartyPhoneChange}
                                                                required
                                                                placeholder="(999) 999-9999"
                                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                    referringPartyPhoneError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                                }`}
                                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                            />
                                                            {referringPartyPhoneError && (
                                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                    </svg>
                                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                        {referringPartyPhoneError}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-left">
                                                        <label htmlFor="referringPartyEmail" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                           Referring Party Email <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                id="referringPartyEmail"
                                                                type="email"
                                                                value={referringPartyEmail}
                                                                onChange={handleReferringPartyEmailChange}
                                                                required
                                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                    referringPartyEmailError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                                }`}
                                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                            />
                                                            {referringPartyEmailError && (
                                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                    </svg>
                                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                        {referringPartyEmailError}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {/* Provider Name moved inside the referring party section */}
                                    <div className="">
                                        { selectedClinic && (selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                            || selectedClinic?.speciality?.toLowerCase() === 'non-profit') && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* OB/GYN Name field */}
                                                <div className="text-left">
                                                    <label htmlFor="providerName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        OB/GYN Name
                                                    </label>
                                                    <div className="relative">
                                                        {selectedClinic && selectedClinic.clinicianNames && selectedClinic.clinicianNames.length > 0 ? (
                                                            <>
                                                                <select
                                                                    id="providerName"
                                                                    value={selectedProvider}
                                                                    onChange={handleProviderSelect}
                                                                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                        providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                                    }`}
                                                                    style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                                >
                                                                    <option value="">Select OB/GYN</option>
                                                                    {selectedClinic.clinicianNames.map((clinician, index) => (
                                                                        <option key={index} value={clinician}>
                                                                            {clinician}
                                                                        </option>
                                                                    ))}
                                                                    <option value="Other">Other</option>
                                                                </select>
                                                                
                                                                {selectedProvider === 'Other' && (
                                                                    <div className="mt-2">
                                                                        <input
                                                                            id="otherProviderName"
                                                                            type="text"
                                                                            value={otherProviderName}
                                                                            onChange={handleOtherProviderNameChange}
                                                                            placeholder="Enter OB/GYN name"
                                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                                providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                                            }`}
                                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <input
                                                                id="providerName"
                                                                type="text"
                                                                value={providerName}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.value;
                                                                    setProviderName(newValue);
                                                                    validateNoSpecialChars(newValue, setProviderNameError);
                                                                }}
                                                                placeholder="Enter provider name"
                                                                required={false}
                                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                    providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                                }`}
                                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                            />
                                                        )}
                                                        
                                                        {providerNameError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {providerNameError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* OB/GYN Phone field */}
                                                <div className="text-left">
                                                    <label htmlFor="providerPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        OB/GYN Phone
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="providerPhone"
                                                            type="tel"
                                                            value={providerPhone}
                                                            onChange={handleProviderPhoneChange}
                                                            placeholder="(999) 999-9999"
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                                providerPhoneError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                        />
                                                        {providerPhoneError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {providerPhoneError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-md">
                                    <div className="mb-2">
                                        <label className="block text-sm font-medium text-gray-700 text-left">
                                            Who's Making the Referral? <span className="text-red-500">*</span>
                                        </label>
                                        <p className="text-sm text-gray-500">(Enter the name/phone/address of your health system, clinic or provider referring the patient)</p>
                                    </div>
                                    <SearchClinic 
                                        ref={searchClinicRef}
                                        onSelectClinic={handleClinicSelect} 
                                        autoFocus={true} 
                                    />
                                </div>
                            )}
                        </div>
                        
                        <div className="p-4 mb-6" style={{ border: 'none', borderLeft: '4px solid #2b5750', paddingTop: '0' }}>
                            <legend className="text-[18px] font-medium text-gray-600 bg-white mb-3">Patient Information</legend>
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="text-left">
                                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="firstName"
                                                type="text"
                                                value={firstName}
                                                onChange={handleFirstNameChange}
                                                required
                                                className={`w-full border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white padding-[.28rem_.8rem] text-[1rem]!important leading-[0rem] ${
                                                    firstNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                            {firstNameError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {firstNameError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="text-left">
                                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="lastName"
                                                type="text"
                                                value={lastName}
                                                onChange={handleLastNameChange}
                                                required
                                                className={`w-full border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                    lastNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                            {lastNameError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {lastNameError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="text-left">
                                        <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Date of Birth <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker 
                                            key={`dob-datepicker-${formKey}`}
                                            onChange={onDOBChange}
                                            placeholder="MM-DD-YYYY"
                                            format="MM-DD-YYYY"
                                            inputReadOnly={false}
                                            allowClear={true}
                                            value={dob ? dayjs(dob, 'MM-DD-YYYY') : null}
                                            style={{ width: '100%', padding: '0.4rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem', border: '1px solid #aeaeae' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="text-left">
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Phone <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="phone"
                                                type="tel"
                                                value={phone}
                                                onChange={handlePhoneChange}
                                                required
                                                className={`w-full border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                    phoneError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                placeholder="(999) 999-9999"
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                            {phoneError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {phoneError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="text-left">
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={handleEmailChange}
                                                required
                                                className={`w-full border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                    emailError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                            {emailError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {emailError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-4 mb-6" style={{ border: 'none', borderLeft: '4px solid #2b5750', paddingTop: '0' }}>
                            <legend className="text-[18px] font-medium text-gray-600 bg-white mb-3">Coverage Information</legend>
                            <div className="grid grid-cols-1 gap-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="text-left">
                                        <label htmlFor="insuranceName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Insurance Name
                                        </label>
                                        <div className="relative">
                                            {/* Previous input implementation  */}
                                            <input
                                                id="insuranceName"
                                                type="text"
                                                value={insuranceName}
                                                onChange={handleInsuranceNameChange}
                                                disabled={selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                    || selectedClinic?.speciality?.toLowerCase() === 'non-profit'}
                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] ${
                                                    selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                        || selectedClinic?.speciality?.toLowerCase() === 'non-profit' 
                                                        ? 'bg-gray-100' 
                                                        : 'bg-white'
                                                } ${
                                                    insuranceNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                           
                                            
                                            {/* <select
                                                id="insuranceName"
                                                value={insuranceName}
                                                onChange={handleInsuranceNameChange}
                                                disabled={selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                    || selectedClinic?.speciality?.toLowerCase() === 'non-profit'}
                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 ${
                                                    selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                        || selectedClinic?.speciality?.toLowerCase() === 'non-profit' 
                                                        ? 'bg-gray-100' 
                                                        : 'bg-white'
                                                } ${
                                                    insuranceNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ 
                                                    padding: '0.6rem 0.8rem', 
                                                    borderRadius: '.25rem', 
                                                    fontSize: '.8rem'
                                                }}
                                            >
                                                <option value="">Select Insurance</option>
                                                {isLoadingInsurances ? (
                                                    <option value="" disabled>Loading insurances...</option>
                                                ) : (
                                                    insuranceOptions?.map((option) => (
                                                        <option key={option?.id} value={option?.name}>
                                                            {option?.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select> */}
                                            
                                            {insuranceNameError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {insuranceNameError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="text-left">
                                        <label htmlFor="insuranceId" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            Insurance ID {(selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                || selectedClinic?.speciality?.toLowerCase() === 'non-profit') && <span className="text-red-500">*</span>}
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="insuranceId"
                                                type="text"
                                                value={insuranceId}
                                                onChange={handleInsuranceIdChange}
                                                required={selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                                    || selectedClinic?.speciality?.toLowerCase() === 'non-profit'}
                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                    insuranceIdError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                            {insuranceIdError && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                        {insuranceIdError}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Case Manager section for insurer or Non-Profit */}
                                { selectedClinic && (selectedClinic?.speciality?.toLowerCase() === 'insurer' 
                                    || selectedClinic?.speciality?.toLowerCase() === 'non-profit') && (
                                    <>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="text-left">
                                                <label htmlFor="caseManagerName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                    Case Manager Name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="caseManagerName"
                                                        type="text"
                                                        value={caseManagerName}
                                                        onChange={handleCaseManagerNameChange}
                                                        required
                                                        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                            caseManagerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                        }`}
                                                        style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                                    />
                                                    {caseManagerNameError && (
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                            <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                            </svg>
                                                            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                {caseManagerNameError}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="text-left">
                                                    <label htmlFor="caseManagerPhone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                    Case Manager Phone <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="caseManagerPhone"
                                                            type="tel"
                                                            value={caseManagerPhone}
                                                            onChange={handleCaseManagerPhoneChange}
                                                            required
                                                            placeholder="(999) 999-9999"
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                                caseManagerPhoneError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                                        />
                                                        {caseManagerPhoneError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {caseManagerPhoneError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-left">
                                                    <label htmlFor="caseManagerEmail" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        Case Manager Email <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="caseManagerEmail"
                                                            type="email"
                                                            value={caseManagerEmail}
                                                            onChange={handleCaseManagerEmailChange}
                                                            required
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                                caseManagerEmailError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                                        />
                                                        {caseManagerEmailError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {caseManagerEmailError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expected Due Date and Risk fields side by side */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Expected Due Date field */}
                                                <div className="text-left">
                                                    <label htmlFor="expectedPostpartumDueDate" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        Expected Due Date or Postpartum
                                                    </label>
                                                    <div className="relative">
                                                        <DatePicker 
                                                        key={`expected-due-date-${formKey}`}
                                                        onChange={handleExpectedPostpartumDueDateChange} 
                                                        placeholder="MM-DD-YYYY"
                                                        format="MM-DD-YYYY"
                                                        inputReadOnly={false}
                                                        allowClear={true}
                                                        value={expectedPostpartumDueDate ? dayjs(expectedPostpartumDueDate, 'MM-DD-YYYY') : null}
                                                        style={{ width: '100%', padding: '0.4rem 0.8rem', borderRadius: '.25rem', fontSize: '1rem', lineHeight: '0rem', border: '1px solid #aeaeae' }} />
                                                        {expectedPostpartumDueDateError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {expectedPostpartumDueDateError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Risk field */}
                                                <div className="text-left">
                                                    <label htmlFor="postpartumRisk" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                        Risk
                                                    </label>
                                                    <div className="relative">
                                                        <select
                                                            id="postpartumRisk"
                                                            value={postpartumRisk}
                                                            onChange={handlePostpartumRiskChange}
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important bg-white ${
                                                                postpartumRiskError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '1rem', lineHeight: '0rem' }}
                                                        >
                                                            <option value="">Select Risk Level</option>
                                                            <option value="Wellness">Wellness</option>
                                                            <option value="Low Risk">Low Risk</option>
                                                            <option value="Medium Risk">Medium Risk</option>
                                                            <option value="High Risk">High Risk</option>
                                                        </select>
                                                        {postpartumRiskError && (
                                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                                    {postpartumRiskError}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div className="p-4 mb-6" style={{ border: 'none', borderLeft: '4px solid #2b5750', paddingTop: '0' }}>
                            <legend className="text-[18px] font-medium text-gray-600 bg-white mb-3">Reason for Referral</legend>
                            <div className="text-left">
                                <div className="relative">
                                    <input
                                        id="reason"
                                        type="text"
                                        value={reason}
                                        onChange={handleReasonChange}
                                        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                            reasonError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                        }`}
                                        style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                    />
                                    {reasonError && (
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                            <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                {reasonError}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Adding Name of Referring Provider field */}
                            { selectedClinic && (selectedClinic?.speciality?.toLowerCase() !== 'insurer' 
                                && selectedClinic?.speciality?.toLowerCase() !== 'non-profit') && (
                                <div className="text-left mt-4">
                                    <label htmlFor="providerName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                        Name of Referring Provider 
                                    </label>
                                    <div className="relative">
                                        {selectedClinic && selectedClinic.clinicianNames && selectedClinic.clinicianNames.length > 0 ? (
                                            <>
                                                <select
                                                    id="providerName"
                                                    value={selectedProvider}
                                                    onChange={handleProviderSelect}
                                                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 bg-white ${
                                                        providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                    }`}
                                                    style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', fontSize: '.8rem', lineHeight: '0rem' }}
                                                >
                                                    <option value="">Select a provider</option>
                                                    {selectedClinic.clinicianNames.map((clinician, index) => (
                                                        <option key={index} value={clinician}>
                                                            {clinician}
                                                        </option>
                                                    ))}
                                                    <option value="Other">Other</option>
                                                </select>
                                                
                                                {selectedProvider === 'Other' && (
                                                    <div className="mt-2">
                                                        <input
                                                            id="otherProviderName"
                                                            type="text"
                                                            value={otherProviderName}
                                                            onChange={handleOtherProviderNameChange}
                                                            required={false}
                                                            placeholder="Enter name of referring provider"
                                                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                                providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                            }`}
                                                            style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <input
                                                id="providerName"
                                                type="text"
                                                value={providerName}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setProviderName(newValue);
                                                    validateNoSpecialChars(newValue, setProviderNameError);
                                                }}
                                                placeholder="Enter name of referring provider"
                                                required={false}
                                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-800 text-[1rem]!important leading-[0rem] bg-white ${
                                                    providerNameError ? 'border-red-500 focus:ring-red-500 pr-10' : 'border-[#aeaeae] focus:ring-blue-500'
                                                }`}
                                                style={{ padding: '0.6rem 0.8rem', borderRadius: '.25rem', lineHeight: '0rem' }}
                                            />
                                        )}
                                        
                                        {providerNameError && (
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 group">
                                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-48 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                                    {providerNameError}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 bottom-0 bg-[#FFF] rounded-b-lg">
                        <div className="flex justify-between">
                            <button
                                type="button"
                                onClick={handleClearForm}
                                style={{ backgroundColor: "#1d4a46", color: "white" }}
                                className="px-6 py-3 font-medium rounded-md shadow-md hover:bg-[#2a6b64] focus:outline-none focus:ring-2 focus:ring-[#1d4a46] focus:ring-offset-2 transition-all duration-200 flex items-center hover:shadow-lg active:transform active:translate-y-px"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Start Over
                            </button>
                            
                            {/* Show different buttons based on form state */}
                            {isFormValid && !isSubmitting ? (
                                <button
                                    type="submit"
                                    style={{ backgroundColor: "#1d4a46", color: "white" }}
                                    className="px-6 py-3 font-medium rounded-md shadow-md hover:bg-[#2a6b64] focus:outline-none focus:ring-2 focus:ring-[#1d4a46] focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg flex items-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Submit Referral
                                </button>
                            ) : isSubmitting ? (
                                <button
                                    type="button"
                                    disabled
                                    className="px-6 py-3 font-medium rounded-md shadow-md bg-[#CCCED1] text-gray-700 opacity-75 cursor-not-allowed flex items-center"
                                >
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </button>
                            ) : (
                                <div className="relative group">
                                    <button
                                        type="button"
                                        disabled
                                        className="px-6 py-0 md:py-3 font-medium rounded-md shadow-md bg-[#CCCED1] text-gray-700 cursor-not-allowed flex items-center"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        Submit Referral
                                    </button>
                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 bg-red-100 border border-red-200 text-red-800 text-xs p-2 rounded shadow-lg z-50">
                                        Please provide data for all the required fields
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    </div>
                </form>
            </div>
        </>
    );
}

export default CreateReferral; 